import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { prospectSearchApi } from '../api/prospectSearchApi';
import type { SearchParameters } from '../types';

const KEYS = {
  sessions: (orgId: string) => ['prospect-search', 'sessions', orgId] as const,
  session: (sessionRef: string) => ['prospect-search', 'session', sessionRef] as const,
  messages: (orgId: string, sessionRef?: string) =>
    ['prospect-search', 'messages', orgId, sessionRef] as const,
  agents: ['prospect-search', 'agents'] as const,
};

export function useTriggerSearch() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: {
      query: string;
      organizationId: string;
      parameters?: SearchParameters;
    }) => prospectSearchApi.triggerSearch(payload),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: KEYS.sessions(variables.organizationId),
      });
    },
  });
}

export function useSessions(organizationId: string | undefined) {
  return useQuery({
    queryKey: KEYS.sessions(organizationId ?? ''),
    queryFn: () => prospectSearchApi.getSessions(organizationId ?? ''),
    enabled: !!organizationId,
    staleTime: 5 * 60_000,
  });
}

export function useSession(sessionRef: string | undefined, organizationId: string | undefined) {
  return useQuery({
    queryKey: [...KEYS.session(sessionRef ?? ''), organizationId ?? ''],
    queryFn: () => prospectSearchApi.getSession(sessionRef ?? '', organizationId ?? ''),
    enabled: !!sessionRef && !!organizationId,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      if (status === 'completed' || status === 'failed' || status === 'cancelled') {
        return false;
      }
      return 3000;
    },
  });
}

export function useMessages(organizationId: string | undefined, sessionRef?: string) {
  return useQuery({
    queryKey: KEYS.messages(organizationId ?? '', sessionRef),
    queryFn: () => prospectSearchApi.getMessages(organizationId ?? '', sessionRef),
    enabled: !!organizationId && !!sessionRef,
    staleTime: 5 * 60_000,
  });
}

export function useCancelSearch() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: { sessionRef: string; organizationId: string }) =>
      prospectSearchApi.cancelSearch(payload.sessionRef, payload.organizationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['prospect-search'] });
    },
  });
}

export function useResearchAgents() {
  return useQuery({
    queryKey: KEYS.agents,
    queryFn: () => prospectSearchApi.getAgents(),
    staleTime: 60_000,
  });
}
